import React, { useMemo } from 'react';
import * as THREE from 'three';
import { tex } from './textures';
import { Box, Mat, ORANGE } from './Facade';

const pitched = (S, pitch, mono) => {
  const h = Math.tan((Math.max(pitch, mono ? 6 : 12) * Math.PI) / 180) * (mono ? S : S / 2);
  const s = new THREE.Shape();
  if (mono) { s.moveTo(-0.4, 0); s.lineTo(S + 0.4, 0); s.lineTo(S + 0.4, h); s.lineTo(-0.4, 0.15); }
  else { s.moveTo(-0.4, 0); s.lineTo(S / 2, h); s.lineTo(S + 0.4, 0); }
  s.closePath();
  return s;
};

const PitchedRoof = ({ wing, top, roof, facade, hl, clip }) => {
  const alongX = wing.w >= wing.d;
  const S = alongX ? wing.d : wing.w;
  const L = alongX ? wing.w : wing.d;
  const geo = useMemo(() => new THREE.ExtrudeGeometry(pitched(S, roof.pitch_deg, roof.type === 'mono'), { depth: L + 0.8, bevelEnabled: false }), [S, L, roof.pitch_deg, roof.type]);
  const mats = useMemo(() => {
    const m = (map, color) => new THREE.MeshStandardMaterial({ map, color: color || '#ffffff', roughness: 0.8, clippingPlanes: clip, clipShadows: true });
    return hl ? [m(null, ORANGE), m(null, ORANGE)] : [m(tex(facade, 1, 1)), m(tex('tiles', 1, 1))];
  }, [facade, hl, clip]);
  const pos = alongX ? [wing.x - 0.4, top, wing.y + wing.d] : [wing.x, top, wing.y - 0.4];
  return <mesh geometry={geo} material={mats} position={pos} rotation={[0, alongX ? Math.PI / 2 : 0, 0]} castShadow receiveShadow />;
};

const units = (wing) => {
  const n = Math.max(1, Math.min(6, Math.round((wing.w * wing.d) / 160)));
  return Array.from({ length: n }, (_, i) => {
    const fx = ((i * 0.37 + 0.21) % 0.7) + 0.15;
    const fy = ((i * 0.53 + 0.33) % 0.6) + 0.2;
    return [wing.x + wing.w * fx, wing.y + wing.d * fy, i % 3 === 0];
  });
};

const FlatRoof = ({ wing, top, facade, hl, clip }) => {
  const pT = 0.3;
  const par = [
    { a: [wing.w + pT, 0.6, pT], p: [wing.x + wing.w / 2, top + 0.3, wing.y] },
    { a: [wing.w + pT, 0.6, pT], p: [wing.x + wing.w / 2, top + 0.3, wing.y + wing.d] },
    { a: [pT, 0.6, wing.d], p: [wing.x, top + 0.3, wing.y + wing.d / 2] },
    { a: [pT, 0.6, wing.d], p: [wing.x + wing.w, top + 0.3, wing.y + wing.d / 2] },
  ];
  return (
    <group>
      <Box args={[wing.w, 0.14, wing.d]} position={[wing.x + wing.w / 2, top + 0.07, wing.y + wing.d / 2]}>
        <Mat map={tex('roof', wing.w, wing.d)} hl={hl} clip={clip} roughness={0.95} />
      </Box>
      {par.map((s, i) => (
        <group key={i}>
          <Box args={s.a} position={s.p}><Mat map={tex(facade, Math.max(s.a[0], s.a[2]), 0.6)} hl={hl} clip={clip} /></Box>
          <Box args={[s.a[0] + 0.06, 0.06, s.a[2] + 0.06]} position={[s.p[0], top + 0.63, s.p[2]]}><Mat color="#8d8a86" hl={hl} clip={clip} metalness={0.5} roughness={0.4} /></Box>
        </group>
      ))}
      {!hl && units(wing).map(([x, y, sky], i) => (sky
        ? <Box key={i} args={[3.2, 0.35, 1.6]} position={[x, top + 0.32, y]}><Mat color="#1d2a33" clip={clip} metalness={0.8} roughness={0.1} envMapIntensity={1.5} /></Box>
        : (
          <group key={i}>
            <Box args={[2.4, 1.2, 1.5]} position={[x, top + 0.74, y]}><Mat color="#9a9da1" clip={clip} metalness={0.6} roughness={0.35} /></Box>
            <Box args={[0.8, 0.1, 0.8]} position={[x + 0.5, top + 1.38, y]}><Mat color="#3a3c3f" clip={clip} metalness={0.6} /></Box>
          </group>
        )))}
    </group>
  );
};

export const WingRoof = ({ wing, top, facade, hl, clip }) => {
  const roof = wing.roof || { type: 'flat', pitch_deg: 0 };
  return roof.type === 'flat'
    ? <FlatRoof wing={wing} top={top} facade={facade} hl={hl} clip={clip} />
    : <PitchedRoof wing={wing} top={top} roof={roof} facade={facade} hl={hl} clip={clip} />;
};
